import { View, Text, styled, useTheme } from 'tamagui'
import { useServerStore } from '@/store'
import { voiceChannelParticipants, messages } from '@/mocks'

const PreviewCard = styled(View, {
  width: 280,
  backgroundColor: '$surfaceRaised',
  borderRadius: '$borderRadiusLg',
  borderWidth: 1,
  borderColor: '$borderDefault',
  padding: 12,
  // @ts-expect-error -- web pointer-events
  style: {
    pointerEvents: 'none',
  },
})

const ServerName = styled(Text, {
  fontFamily: '$fontHeading',
  fontWeight: '600',
  fontSize: 16,
  color: '$surfaceForeground',
  marginBottom: 4,
})

const MemberCount = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 12,
  color: '$sidebarMuted',
  marginBottom: 8,
})

const PreviewSection = styled(View, {
  borderTopWidth: 1,
  borderTopColor: '$borderDefault',
  paddingTop: 8,
  marginTop: 4,
  gap: 4,
})

const PreviewRow = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 6,
})

const PreviewLabel = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 12,
  color: '$color8',
})

const PreviewValue = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 12,
  color: '$sidebarMuted',
  flex: 1,
  overflow: 'hidden',
})

interface ServerPreviewProps {
  serverId: string
}

export function ServerPreview({ serverId }: ServerPreviewProps) {
  const server = useServerStore((s) => s.servers[serverId])
  const theme = useTheme()
  if (!server) return null

  const textChannels = server.channels.filter(c => c.type === 'text')
  const activeChannel = textChannels.find(c => c.unreadCount > 0) || textChannels[0]

  const voiceChannels = server.channels.filter(c => c.type === 'voice')
  const activeVoice = voiceChannels.filter(c => {
    const p = voiceChannelParticipants[c.id]
    return p && p.length > 0
  })

  const channelMsgs = activeChannel ? messages[activeChannel.id] : undefined
  const lastMsg = channelMsgs?.[channelMsgs.length - 1]

  return (
    <PreviewCard
      // @ts-expect-error -- web box-shadow
      style={{ boxShadow: theme.shadowCardHover?.val || 'none' }}
    >
      <ServerName>{server.name}</ServerName>
      <MemberCount>👤 {server.memberCount} members</MemberCount>

      <PreviewSection>
        {activeChannel && (
          <PreviewRow>
            <PreviewLabel># {activeChannel.name}</PreviewLabel>
            {lastMsg && (
              <PreviewValue numberOfLines={1}>
                {lastMsg.content.slice(0, 50)}
                {lastMsg.content.length > 50 ? '...' : ''}
              </PreviewValue>
            )}
          </PreviewRow>
        )}

        {activeVoice.map((vc) => {
          const participants = voiceChannelParticipants[vc.id] || []
          return (
            <PreviewRow key={vc.id}>
              <Text color="$warning" fontSize={12}>🔊</Text>
              <PreviewLabel>{vc.name}</PreviewLabel>
              <PreviewValue>
                {participants.length} connected
              </PreviewValue>
            </PreviewRow>
          )
        })}

        {server.unreadCount > 0 && (
          <PreviewRow>
            <Text color="$accent" fontSize={11}>●</Text>
            <PreviewValue>
              {server.unreadCount} unread
              {server.mentionCount > 0 && `, ${server.mentionCount} mentions`}
            </PreviewValue>
          </PreviewRow>
        )}
      </PreviewSection>
    </PreviewCard>
  )
}
